/**
 * @file useDesktopSyncDownloadUrl.ts
 * @description Where the organization publishes Syncretic File Sync, or null
 * when it has not said. Consumer: `AttachmentSyncNudge`, which renders nothing
 * without it.
 *
 * `desktopSyncDownloadUrl` is not a declared field on `OrganizationParameters`
 * and is deliberately not being added: nothing in `system` produces the app, so
 * the URL is an organization's own fact. Until an admin writes one the nudge is
 * dark for everyone, which is the honest reading of a value nobody has set —
 * the same stance `useDesktopAgentPresence` takes about a heartbeat nobody
 * writes. The value arrives as `unknown` through the store's index signature and
 * is narrowed here, so no cast asserts a guarantee the document cannot give.
 *
 * Only an `https://` URL is accepted. The value reaches an `href`, and refusing
 * every other scheme at the one place that reads it is cheaper than trusting
 * whatever an admin form once stored.
 */

import { useEffect } from 'react';
import { useSnapshot } from 'valtio';

import { orgParametersStore } from '../../../use/useOrganizationParameters';

const DOWNLOAD_URL_PARAMETER = 'desktopSyncDownloadUrl';

export const useDesktopSyncDownloadUrl = (): string | null => {
    useEffect(() => {
        orgParametersStore.connect();
        return () => orgParametersStore.disconnect();
    }, []);

    const snap = useSnapshot(orgParametersStore.state);
    const configured: unknown = snap.data?.[DOWNLOAD_URL_PARAMETER];

    if (typeof configured !== 'string') {
        return null;
    }
    const trimmed = configured.trim();
    return trimmed.startsWith('https://') ? trimmed : null;
};
